import { Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AllergenSource, AllergenStatus, RecipeAllergenSummary } from '../types';

interface RecipeBankAllergensTabProps {
  allergens: RecipeAllergenSummary[];
}

const statusColor: Record<AllergenStatus, 'error' | 'warning' | 'success' | 'default'> = {
  [AllergenStatus.Contains]: 'error',
  [AllergenStatus.MayContain]: 'warning',
  [AllergenStatus.Unknown]: 'default',
  [AllergenStatus.NotPresent]: 'success',
  [AllergenStatus.FreeFrom]: 'success',
};

const normalizeStatus = (status: string): AllergenStatus => {
  const normalized = status.replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'maycontain') return AllergenStatus.MayContain;
  if (normalized === 'notpresent') return AllergenStatus.NotPresent;
  if (normalized === 'freefrom') return AllergenStatus.FreeFrom;
  if (normalized === 'unknown') return AllergenStatus.Unknown;
  return AllergenStatus.Contains;
};

const statusLabelKey = (status: string) => {
  const normalized = normalizeStatus(status);
  if (normalized === AllergenStatus.MayContain) return 'mayContain';
  if (normalized === AllergenStatus.NotPresent) return 'notPresent';
  if (normalized === AllergenStatus.FreeFrom) return 'freeFrom';
  if (normalized === AllergenStatus.Unknown) return 'unknown';
  return 'contains';
};

const statusChipColor = (status: string) => statusColor[normalizeStatus(status)];

// Recipe overrides are user-entered; show "Manual" rather than the raw backend source value.
const sourceLabel = (t: (key: string) => string, source: string) =>
  (source === AllergenSource.RecipeOverride ? t('allergens.sourceManual') : source);

/**
 * Read-only per-product allergen traceability - same shape as the authoring Allergens tab's
 * table, minus overrides/edit actions. Shows which component(s) introduced each allergen.
 */
const RecipeBankAllergensTab = ({ allergens }: RecipeBankAllergensTabProps) => {
  const { t } = useTranslation('recipeV2');
  const sortedAllergens = [...allergens].sort((left, right) =>
    left.allergenName.localeCompare(right.allergenName) || left.allergenCode.localeCompare(right.allergenCode));

  if (!sortedAllergens.length) {
    return <Typography color="text.secondary">{t('recipeBank.detail.noAllergens', 'No allergen data available for this recipe.')}</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('allergens.allergen')}</TableCell>
            <TableCell>{t('statusLabel')}</TableCell>
            <TableCell>{t('source')}</TableCell>
            <TableCell>{t('allergens.introducedBy')}</TableCell>
            <TableCell>{t('allergens.path')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAllergens.map((allergen) =>
            allergen.introducedBy.length > 0 ? (
              allergen.introducedBy.map((trace, idx) => (
                <TableRow key={`${allergen.allergenCode}-${idx}`}>
                  {idx === 0 ? (
                    <>
                      <TableCell rowSpan={allergen.introducedBy.length}>
                        <Chip label={allergen.allergenName}
                          size="small"
                          color={statusChipColor(allergen.status)} />
                      </TableCell>
                      <TableCell rowSpan={allergen.introducedBy.length}>
                        {t(`allergens.${statusLabelKey(allergen.status)}`)}
                      </TableCell>
                      <TableCell rowSpan={allergen.introducedBy.length}>
                        {sourceLabel(t, allergen.source)}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell>{trace.componentDescription}</TableCell>
                  <TableCell>
                    <Typography variant="textXS"
                      color="text.secondary">{trace.path}</Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow key={allergen.allergenCode}>
                <TableCell>
                  <Chip label={allergen.allergenName}
                    size="small"
                    color={statusChipColor(allergen.status)} />
                </TableCell>
                <TableCell>{t(`allergens.${statusLabelKey(allergen.status)}`)}</TableCell>
                <TableCell>{sourceLabel(t, allergen.source)}</TableCell>
                <TableCell colSpan={2}>
                  <Typography variant="textSM"
                    color="text.secondary">—</Typography>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RecipeBankAllergensTab;
